import { useAiAssistantStore } from '~/stores/aiAssistantStore';
import { AI_ROLE } from '~/stores/aiAssistantTypes';
import { useEvent } from '~/composables/useEventBus';
import { AI_ENDPOINTS, useAiEndpoints } from '~/composables/useAiEndpoints';

const WS_EVENTS = ['text_delta', 'tool_call', 'interactive_choices', 'invoice_action', 'contact_created', 'contact_updated', 'worksite_created', 'worksite_updated', 'search_results', 'stream_end', 'verification_step', 'verification_complete', 'verification_auto_fixing', 'verification_auto_fix_reply'] as const;

interface ChatOptions {
    message: string;
    conversationId?: string | null;
    conversableType?: string | null;
    conversableId?: string | null;
    metadata?: Record<string, any> | null;
}

interface ChatResponse {
    conversation_id: string | null;
    message: string | null;
    document_created: { type: string; id: string } | null;
    all_documents_created: Array<{ type: string; id: string }> | null;
    interactive: { interactive: boolean; questions: Array<{ type: string; question: string; options: Array<{ label: string; value: string }> }>; status: string } | null;
    bubble_intro?: string | null;
    conversation_title?: string | null;
}

// Module-scope state: shared across all useAiChat() instances so that
// abort() from any consumer cancels the actual in-flight request.
let abortController: AbortController | null = null;
let subscribedChannel: any = null;
let bufferedChoices: Array<{ type: string; question: string; options: Array<{ label: string; value: string }> }> | null = null;

export const useAiChat = () => {
    const store = useAiAssistantStore();
    const { t } = useTranslation();
    const { $websocket } = useNuxtApp();
    const { url, jsonHeaders } = useAiEndpoints();

    const pendingInvoiceAction = ref(false);

    const handleWsEvent = (eventName: string, event: Record<string, any>) => {
        if (import.meta.dev) console.debug('[AI-WS] event', eventName, event);

        switch (eventName) {
            case 'text_delta': {
                const delta = event.delta ?? event.text ?? '';
                // eslint-disable-next-line no-console
                console.debug('[AI-WS] text_delta', { len: delta.length, preview: delta.slice(0, 30) });
                store.appendStreamDelta(delta);
                break;
            }
            case 'tool_call':
                store.addToolCall(event.tool_name ?? event.name ?? 'unknown');
                break;
            case 'interactive_choices':
                // Buffer choices — they'll be shown after the HTTP response text arrives
                if (event.questions?.length) {
                    bufferedChoices = event.questions;
                }
                break;
            case 'search_results':
                if (event.action) {
                    store.addToolCall('search_' + event.action);
                }
                break;
            case 'invoice_action':
                pendingInvoiceAction.value = true;
                useEvent('invoicing:invoice:ai-refresh');
                if (event.document_type && event.invoice_id) {
                    useEvent('ai:entity-modified', { type: event.document_type, id: event.invoice_id });
                    if (event.action === 'create_draft' || event.action === 'replicate') {
                        useEvent('ai:entity-created', { type: event.document_type, id: event.invoice_id, display_name: event.title });
                        store.autoPreview(event.document_type, event.invoice_id);
                    }
                }
                break;
            case 'contact_created':
                useEvent('invoicing:invoice:ai-refresh');
                if (event.id) {
                    useEvent('ai:entity-created', { type: 'contact', id: event.id, display_name: event.display_name });
                    useEvent('ai:entity-modified', { type: 'contact', id: event.id });
                    if (!pendingInvoiceAction.value) {
                        store.autoPreview('contact', event.id);
                    }
                }
                break;
            case 'contact_updated':
                useEvent('invoicing:invoice:ai-refresh');
                if (event.id) {
                    useEvent('ai:entity-modified', { type: 'contact', id: event.id });
                }
                break;
            case 'worksite_created':
                useEvent('invoicing:invoice:ai-refresh');
                if (event.id) {
                    useEvent('ai:entity-created', { type: 'worksite', id: event.id, display_name: event.title });
                    useEvent('ai:entity-modified', { type: 'worksite', id: event.id });
                    if (!pendingInvoiceAction.value) {
                        store.autoPreview('worksite', event.id);
                    }
                }
                break;
            case 'worksite_updated':
                useEvent('invoicing:invoice:ai-refresh');
                if (event.id) {
                    useEvent('ai:entity-modified', { type: 'worksite', id: event.id });
                }
                break;
            case 'stream_end':
                pendingInvoiceAction.value = false;
                break;
            case 'verification_step':
                store.updateVerificationStep(event.document_id, {
                    step: event.step,
                    total: event.total,
                    label: event.label,
                    status: event.status,
                    message: event.message,
                    rule: event.rule,
                    subSteps: event.sub_results ?? undefined,
                });
                break;
            case 'verification_auto_fixing':
                store.startVerificationAutoFixing(event.document_id);
                break;
            case 'verification_auto_fix_reply': {
                if (event.message) {
                    // Land the reply after the verification card animation drains so the
                    // chip+bubble don't pop in mid-stagger.
                    const total = store.verifications.get(event.document_id)?.steps[0]?.total ?? 0;
                    const delayMs = 400 * (total + 1) + 400;
                    setTimeout(() => {
                        store.setVerificationAutoFixing(event.document_id, false);
                        store.addMessage(AI_ROLE.ASSISTANT, event.message, {
                            autoFix: { applied: event.applied === true, dismissed: event.dismissed === true },
                        });
                        // Minimise the (expanded) verification card so the reply, rendered
                        // above it in the chat flow, is visible without scrolling on small screens.
                        store.minimizeVerification(event.document_id);
                    }, delayMs);
                }
                break;
            }
            case 'verification_complete': {
                store.completeVerification(event.document_id, event.status);

                if (event.status === 'issues_found' && Array.isArray(event.issues) && event.issues.length > 0) {
                    // Issue messages are already rendered inline under each failed step in the
                    // verification card — auto-open the preview after the animation drains so
                    // the user can review the document directly.
                    const total = store.verifications.get(event.document_id)?.steps[0]?.total ?? 0;
                    const delayMs = 400 * (total + 1) + 400;
                    setTimeout(() => {
                        if (event.document_type && event.document_id) {
                            store.autoPreview(event.document_type, event.document_id);
                        }
                        useEvent('invoicing:invoice:ai-refresh');
                    }, delayMs);
                }
                break;
            }
        }
    };

    const subscribe = (channelName: string) => {
        if (subscribedChannel) {
            unsubscribe();
        }

        if (import.meta.dev) console.debug('[AI-WS] subscribe', channelName);

        subscribedChannel = $websocket.private(channelName);

        // Pusher channel lifecycle hooks expose auth/subscribe outcomes that Echo otherwise swallows.
        const pusherChannel = subscribedChannel.subscription;
        if (pusherChannel?.bind) {
            pusherChannel.bind('pusher:subscription_succeeded', () => {
                // eslint-disable-next-line no-console
                console.debug('[AI-WS] subscription_succeeded', channelName);
            });
            pusherChannel.bind('pusher:subscription_error', (status: unknown) => {
                // eslint-disable-next-line no-console
                console.error('[AI-WS] subscription_error', channelName, status);
            });
        }

        for (const event of WS_EVENTS) {
            subscribedChannel.listen(`.${event}`, (data: Record<string, any>) => handleWsEvent(event, data));
        }
    };

    const unsubscribe = () => {
        if (!subscribedChannel) return;

        if (import.meta.dev) console.debug('[AI-WS] unsubscribe', subscribedChannel.name);

        for (const event of WS_EVENTS) {
            subscribedChannel.stopListening(`.${event}`);
        }

        $websocket.leave(subscribedChannel.name);
        subscribedChannel = null;
    };

    const sendMessage = async (options: ChatOptions): Promise<ChatResponse | null> => {
        abortController = new AbortController();

        try {
            // eslint-disable-next-line no-console
            console.debug('[AI-WS] sendMessage', { conversationId: options.conversationId, channelSubscribed: !!subscribedChannel });

            const response = await fetch(url(AI_ENDPOINTS.prompt), {
                method: 'POST',
                headers: jsonHeaders(),
                body: JSON.stringify({
                    message: options.message,
                    conversation_id: options.conversationId ?? undefined,
                    conversable_type: options.conversableType ?? undefined,
                    conversable_id: options.conversableId ?? undefined,
                    ...(options.metadata && { metadata: options.metadata }),
                }),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Chat error: ${response.status}`);
            }

            const data: ChatResponse = await response.json();
            const streamingHadContent = store.currentStreamingMessage.length > 0;

            // eslint-disable-next-line no-console
            console.debug('[AI-WS] http_done', {
                streamingHadContent,
                streamingLen: store.currentStreamingMessage.length,
                hasMessage: !!data.message,
                hasInteractive: !!data.interactive?.questions?.length,
                hasDocCreated: !!data.document_created,
                bufferedChoicesCount: bufferedChoices?.length ?? 0,
            });

            let allCreated: Array<{ type: string; id: string }> | undefined;
            if (data.all_documents_created?.length) {
                allCreated = data.all_documents_created;
            } else if (data.document_created) {
                allCreated = [data.document_created];
            }

            // Match backend formatter: render as yellow narration pill when either
            //  (a) interactive_choices arrived without a doc creation (pre-creation thinking), OR
            //  (b) a contact/worksite was created — design treats that ack as a pill, not a
            //      bubble. Invoice/quote/etc. creation keeps the bubble (has its own recap).
            const hasChoices = !!bufferedChoices?.length || !!data.interactive?.questions?.length;
            const createdTypes = (allCreated ?? []).map((d) => d.type);
            const isNonInvoiceEntity = createdTypes.some((t) => t === 'contact' || t === 'worksite');
            const isNarration = (hasChoices && !allCreated?.length) || isNonInvoiceEntity;
            const bubbleIntro = data.bubble_intro ?? null;
            store.finalizeStream(allCreated, isNarration, bubbleIntro);

            // Auto-open preview for the primary created document
            if (data.document_created) {
                store.autoPreview(data.document_created.type, data.document_created.id);
            }

            // Fallback: if WebSocket didn't deliver text this turn, use the HTTP response.
            // Content-equality against the whole history is unsafe — an identical answer earlier
            // in the same conversation would suppress the new turn.
            if (data.message && !streamingHadContent) {
                store.addMessage('assistant', data.message, { documentCreated: allCreated, narration: isNarration, bubbleIntro });
            }

            // Show interactive choices — prefer WS buffer, fall back to HTTP response
            const choices = bufferedChoices ?? (data.interactive?.questions?.length ? data.interactive.questions : null);
            bufferedChoices = null;

            if (choices?.length && !store.hasPendingQuestions) {
                const lastInteractive = store.messages.find((m) => m.type === 'interactive' && !m.optionSelected);
                if (!lastInteractive) {
                    store.addBatchedInteractiveMessages(choices as Array<{ type: string; question: string; options: Array<{ label: string; value: string }> }>);
                }
            }

            if (data.conversation_id) {
                const isNewConversation = !store.conversationId;
                store.conversationId = data.conversation_id;
                if (isNewConversation) {
                    useEvent('ai:conversation-created', { id: data.conversation_id });
                }
            }

            if (data.document_created) {
                store.createdDocumentId = data.document_created.id;
                store.createdDocumentType = data.document_created.type;
            }

            if (store.hadModificationTools || store.createdDocumentId) {
                useEvent('invoicing:invoice:ai-refresh');
            }

            // Patch the sidebar entry so the title (set on first turn from the user message)
            // appears immediately without refetching the whole list.
            if (data.conversation_id) {
                useEvent('ai:conversation-updated', { id: data.conversation_id, title: data.conversation_title ?? null });
            }

            return data;
        } catch (error: unknown) {
            if (error instanceof Error && error.name === 'AbortError') return null;
            store.setError(t('ai.chat.connection_lost'));
            store.finalizeStream();
            return null;
        } finally {
            abortController = null;
        }
    };

    const abort = () => {
        abortController?.abort();
    };

    return {
        subscribe,
        unsubscribe,
        sendMessage,
        abort,
    };
};
